import React, { useState, useEffect } from 'react';
import {
  Button,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  Box
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import styles from '../../styles/OrganizationSignup.module.css';

const PlanSelection = ({ initialPlan, initialInterval, teamSize, onSelect, onBack }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [billingInterval, setBillingInterval] = useState(initialInterval || 'month');
  const [selectedPlanId, setSelectedPlanId] = useState(initialPlan?.id || null);

  useEffect(() => {
    const fetchOrgPlans = async () => {
      try {

        // Use the same endpoint as OrganizationPlans component
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/prices`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            metadataKey: 'type',
            metadataValues: ['organization']
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch plans: ${response.status}`);
        }

        const data = await response.json();

        if (!data || data.length === 0) {
          setError('No organization plans available. Please contact support.');
          setLoading(false);
          return;
        }

        // Sort by display_order
        const sortedPlans = data.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
        setPlans(sortedPlans);
        setLoading(false);
      } catch (error) {
        setError(error.message || 'Failed to load organization plans. Please try again later.');
        setLoading(false);
      }
    };

    fetchOrgPlans();
  }, []);

  const handleBillingChange = (event, newInterval) => {
    if (newInterval !== null) {
      setBillingInterval(newInterval);
    }
  };

  const handlePlanSelect = (plan) => {
    // Check if it's Custom Enterprise (no prices)
    if (plan.prices.length === 0 || plan.name === 'Custom Enterprise') {
      // Redirect to contact sales
      window.location.href = 'mailto:sales@flyzone.com?subject=Enterprise Plan Inquiry';
      return;
    }

    setSelectedPlanId(plan.id);
  };

  const handleContinue = () => {
    const selected = plans.find(p => p.id === selectedPlanId);
    if (selected) {
      onSelect(selected, billingInterval);
    }
  };

  const calculateAnnualSavings = (monthlyPrice, annualPrice) => {
    const monthlyCost = (monthlyPrice / 100) * 12;
    const annualCost = annualPrice / 100;
    const savings = monthlyCost - annualCost;
    const percentage = Math.round((savings / monthlyCost) * 100);
    return percentage;
  };

  const formatPrice = (amount, interval) => {
    if (amount === 0) return 'Custom';
    const price = interval === 'year' ? amount / 12 / 100 : amount / 100;
    return `$${Math.round(price)}`;
  };

  // Calculate average annual savings across all plans
  const getAverageSavings = () => {
    const plansWithBothPrices = plans.filter(plan => {
      const hasMonthly = plan.prices.some(p => p.recurring?.interval === 'month');
      const hasAnnual = plan.prices.some(p => p.recurring?.interval === 'year');
      return hasMonthly && hasAnnual;
    });

    if (plansWithBothPrices.length === 0) return 0;

    const totalSavings = plansWithBothPrices.reduce((sum, plan) => {
      const monthlyPrice = plan.prices.find(p => p.recurring?.interval === 'month');
      const annualPrice = plan.prices.find(p => p.recurring?.interval === 'year');
      if (monthlyPrice && annualPrice) {
        const savings = calculateAnnualSavings(monthlyPrice.unit_amount, annualPrice.unit_amount);
        return sum + savings;
      }
      return sum;
    }, 0);

    return Math.round(totalSavings / plansWithBothPrices.length);
  };

  const averageSavings = getAverageSavings();

  const getRecommendation = (plan) => {
    // Don't recommend Custom Enterprise
    if (plan.prices.length === 0 || plan.name === 'Custom Enterprise') return false;

    const maxUsers = parseInt(plan.metadata?.max_users || '0');
    const teamSizeNum = parseInt(teamSize.split('-')[1] || teamSize.replace('+', ''));

    return teamSizeNum <= maxUsers;
  };

  if (loading) {
    return (
      <div className={styles['loading-container']} role="status" aria-live="polite">
        <CircularProgress aria-label="Loading organization plans" />
        <span className="sr-only">Loading plans...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error" role="alert">
        {error}
      </Alert>
    );
  }

  return (
    <div className={styles['plan-selection']}>
      <div className={styles['form-section']}>
        <h2 className={styles['section-title']}>
          Choose Your Plan
        </h2>
        <p className={styles['section-description']}>
          All plans include a 7-day free trial. No credit card required to start.
        </p>

        {/* Billing Toggle */}
        <div className={styles['billing-toggle-container']}>
          <label id="billing-toggle-label" className={styles['toggle-label']}>
            Billing frequency:
          </label>
          <ToggleButtonGroup
            value={billingInterval}
            exclusive
            onChange={handleBillingChange}
            aria-labelledby="billing-toggle-label"
            className={styles['billing-toggle']}
          >
            <ToggleButton
              value="month"
              aria-label="Monthly billing"
              className={styles['toggle-button']}
            >
              Monthly
            </ToggleButton>
            <ToggleButton
              value="year"
              aria-label={averageSavings > 0 ? `Annual billing - save ${averageSavings} percent` : "Annual billing"}
              className={styles['toggle-button']}
            >
              Annual
              {averageSavings > 0 && (
                <span className={styles['savings-text']} aria-hidden="true">
                  (Save {averageSavings}%)
                </span>
              )}
            </ToggleButton>
          </ToggleButtonGroup>
        </div>

        {/* Plans Grid */}
        <div className={styles['plans-grid']} role="list">
          {plans.map((plan) => {
            const selectedPrice = plan.prices.find(p => p.recurring?.interval === billingInterval);
            const monthlyPrice = plan.prices.find(p => p.recurring?.interval === 'month');
            const annualPrice = plan.prices.find(p => p.recurring?.interval === 'year');
            const isCustom = plan.prices.length === 0 || plan.name === 'Custom Enterprise';
            const isRecommended = getRecommendation(plan);
            const isSelected = selectedPlanId === plan.id;

            const savings = monthlyPrice && annualPrice
              ? calculateAnnualSavings(monthlyPrice.unit_amount, annualPrice.unit_amount)
              : 0;

            return (
              <article
                key={plan.id}
                className={`${styles['plan-card']} ${isSelected ? styles['plan-selected'] : ''} ${isRecommended ? styles['plan-recommended'] : ''}`}
                aria-label={`${plan.name} organization plan`}
                role="listitem"
              >
                {isRecommended && !isCustom && (
                  <div className={styles['recommended-badge']} aria-label="Recommended for your team size">
                    Recommended
                  </div>
                )}

                <header className={styles['plan-header']}>
                  <h3 className={styles['plan-name']}>{plan.name}</h3>

                  {!isCustom && selectedPrice ? (
                    <div className={styles['plan-price']}>
                      <span className={styles['price-amount']}>
                        {formatPrice(selectedPrice.unit_amount, billingInterval)}
                      </span>
                      <span className={styles['price-period']}>/month</span>
                      {billingInterval === 'year' && savings > 0 && (
                        <div className={styles['savings-badge']}>
                          Save {savings}%
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={styles['plan-price']}>
                      <span className={styles['price-amount']}>Custom</span>
                    </div>
                  )}

                  <p className={styles['plan-description']}>{plan.description}</p>
                </header>

                <div className={styles['plan-body']}>
                  <h4 className={styles['features-heading']}>Features included:</h4>
                  <ul className={styles['features-list']}>
                    {plan.features.map((feature, index) => (
                      <li key={index} className={styles['feature-item']}>
                        <CheckCircleIcon
                          className={styles['icon-check']}
                          aria-hidden="true"
                          fontSize="small"
                        />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <footer className={styles['plan-footer']}>
                  <Button
                    variant={isSelected ? 'contained' : 'outlined'}
                    color="primary"
                    fullWidth
                    onClick={() => handlePlanSelect(plan)}
                    aria-label={isCustom ? 'Contact sales for custom enterprise plan' : `Select ${plan.name}`}
                    className={styles['btn-select-plan']}
                  >
                    {isCustom ? 'Contact Sales' : isSelected ? 'Selected' : 'Select Plan'}
                  </Button>
                </footer>
              </article>
            );
          })}
        </div>
      </div>

      <Box className={styles['form-actions']}>
        <Button
          variant="outlined"
          onClick={onBack}
          aria-label="Go back to organization details"
          className={styles['btn-secondary']}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleContinue}
          disabled={!selectedPlanId}
          aria-label="Continue to plan confirmation"
          className={styles['btn-primary']}
        >
          Continue to Review
        </Button>
      </Box>
    </div>
  );
};

export default PlanSelection;
